import { useTranslation } from "react-i18next";
import { cn, Icon } from "@nexus/ui";
import type { ChangelogStage } from "@/features/changelog/lib/changelog-display";

const STAGES: ChangelogStage[] = ["draft", "approved", "sent"];

/**
 * Three steps, because the tab's whole point is that notes are written, then
 * signed off, then sent — and which of those has happened decides every button
 * on the card. Reading it should not require reading the buttons.
 */
export function ChangelogStageIndicator({ stage }: { stage: ChangelogStage }) {
  const { t } = useTranslation();
  const currentIndex = STAGES.indexOf(stage);

  return (
    <ol className="m-0 flex list-none items-center gap-2 p-0" aria-label={t("admin.changelog.stage.label", "Progress")}>
      {STAGES.map((name, index) => {
        const isDone = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <li key={name} className="flex items-center gap-2">
            {index > 0 ? (
              <span className={cn("h-px w-6", isDone || isCurrent ? "bg-primary" : "bg-border")} />
            ) : null}
            <span
              className={cn(
                "flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                isCurrent && "bg-primary-subtle text-primary-subtle-foreground",
                isDone && "text-muted-foreground",
                !isDone && !isCurrent && "text-muted-foreground/60",
              )}
              aria-current={isCurrent ? "step" : undefined}
            >
              {isDone ? <Icon name="check" size="xs" color="inherit" aria-hidden /> : null}
              {t(`admin.changelog.stage.${name}`, { defaultValue: name })}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
